import React, { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useStoreState } from "ezStore";
import { saveAs } from "file-saver";
import { useLazyQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { intl } from "LocaleWrapper";

import { FormGroup, FileUpload, Table, Button, showSnackbar } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import { CustomDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import { ExceptionErrorContent, request } from "components/utils/Request2";
import { useFrontendTable } from "components/table";

import { hasOptionalFieldPermission, Fields } from "imports/Utils";

import RelationSelect from "view/relations/components/selects/RelationSelect";

import { uploadShipsSchema, UploadShipsSchemaInput, UploadShipsSchemaOutput } from "../utils/ship.schema";

import { RelationType } from "__generated__/enums";
import { GetMaxFileSizeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";

type ValidItem = {
	valid: boolean;
	ship: {
		id: number;
	};
}

interface UploadShipsDialogProps {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

const UploadShipsDialog = ({ open, onClose, refetch }: UploadShipsDialogProps) => {

	const { instance } = useCoastguard();

	const [ step, setStep ] = useState<number>(1);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ getMaxFileSize ] = useLazyQuery(GetMaxFileSizeDocument);
	const [ maxFileSize, setMaxFileSize ] = useState<number>(0);
	const [ validateContent, setValidateContent ] = useState<ValidItem[]>([]);

	const defaultValues = {
		operator: null,
		file: [],
	};

	const { control, handleSubmit, reset, setValue } = useForm<UploadShipsSchemaInput, unknown, UploadShipsSchemaOutput>({
		resolver: zodResolver(uploadShipsSchema),
		defaultValues: defaultValues,
	});

	const [ operator ] = useWatch({ control, name: [ "operator" ] });

	const hasInvalidShips =	validateContent.some((it) => !it.valid);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
		setStep(1);
	};

	const onValidate = (data: UploadShipsSchemaOutput) => {

		setLoading(true);
		setValidateContent([]);

		const formData = new FormData();

		formData.append("operatorId", data.operator.id.toString());
		formData.append("uploadedFile", data.file[0]);

		instance.fetch("/ships/excelimport/validate", {
			method: "POST",
			body: formData,
		}).then((response) => {
			response.json().then(data => {
				setValidateContent(data);
				setStep(2);
			});
		}).catch((error) => {
			showSnackbar(<ExceptionErrorContent response={error} />, { variant: "error" });
		}).finally(() => {
			setLoading(false);
		});
	};

	const onSave = (validContent: ValidItem[]) => {
		setLoading(true);

		request({
			method: "POST",
			url: `ships/excelimport/${operator?.id}`,
			body: validContent,
		}).then(() => {
			handleOnClose();
			refetch();
		}).finally(() => setLoading(false));
	};

	const onDownloadTemplate = () => {
		instance.fetchBlob("/ships/excelimport/template", {
			method: "GET",
			headers: {
				Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			},
		}).then(async (blob) => {
			saveAs(blob, "template.xlsx");
		});
	};

	useEffect(() => {
		getMaxFileSize()
			.then(({ data }) => {
				if (!data) {
					return;
				}
				setMaxFileSize(data.maxFileSize * 1000000);
			});
	}, []);

	return (
		<CustomDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CARGOS_UPLOAD_USING_EXCEL" })}
			maxWidth={step === 1 ? "sm" : "lg"}
			actions={(
				<React.Fragment>
					{step === 1 && (
						<Button type="submit"
							form="uploadShipsForm"
							loading={loading}
							size="small">
							{intl.formatMessage({ id: "VALIDATE" })}
						</Button>
					)}

					{step === 2 && (
						<React.Fragment>
							<Button
								onClick={() => {
									setStep(1);
									setValue("file", []);
								}}
								variant="text"
								size="small">
								{intl.formatMessage({ id: "GO_BACK" })}
							</Button>

							<Button
								onClick={() => onSave(validateContent)}
								disabled={hasInvalidShips || loading}
								size="small"
								loading={loading}
							>
								{intl.formatMessage({ id: "SAVE" })}
							</Button>

							{hasInvalidShips && (
								<Button
									disabled={loading || validateContent.every(item => !item.valid)}
									onClick={() => onSave(validateContent.filter(item => item.valid))}
									size="small"
									style={{ marginLeft: 8 }}
									loading={loading}
								>
									{intl.formatMessage({ id: "ACCEPT" })}
								</Button>
							)}
						</React.Fragment>
					)}
				</React.Fragment>
			)}>

			<React.Fragment>
				{step === 1 && (
					<form onSubmit={(event) => void handleSubmit(onValidate)(event)} id="uploadShipsForm">
						<FormGroup label={intl.formatMessage({ id: "OPERATOR" })} overflow="hidden" required>
							<Controller
								name='operator'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<RelationSelect
											value={field.value}
											onChange={field.onChange}
											types={[ RelationType.Operator ]} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label="" overflow="hidden">
							<Controller
								name='file'
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<FileUpload
											maxSize={maxFileSize}
											maxNumberOfFiles={1}
											onChange={dropZoneFiles => field.onChange(dropZoneFiles)}
											accept={{
												"application/vnd.ms-excel": [ ".xlsx" ],
											}}
											onRejected={dropZoneFiles => {
												dropZoneFiles.forEach(item => {

													const code = item.errors[0]?.code;
													const message = item.errors[0]?.message;

													showSnackbar((
														<div>
															<p style={{ margin: 0, fontWeight: 600 }}>{item.file.name}</p>

															{code === "file-too-large" ? (
																<p style={{ margin: 0 }}>{`${intl.formatMessage({ id: "FILE_TO_LARGE" }).replace("%d", (maxFileSize / 1000000).toString())}. ${intl.formatMessage({ id: "CONTACT_ADMIN" })}`}</p>
															) : (
																<p style={{ margin: 0 }}>{message}</p>
															)}

														</div>
													), { variant: "error" });
												});
											}} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<Button onClick={() => onDownloadTemplate()}
							style={{ marginTop: 16 }}
							variant="text"
							size="small">
							{intl.formatMessage({ id: "DOWNLOAD_TEMPLATE" })}
						</Button>
					</form>
				)}

				{step === 2 && (
					<ValidateTable data={validateContent} />
				)}
			</React.Fragment>
		</CustomDialog>
	);
};

const ValidateTable = ({ data }: { data: ValidItem[] }) => {

	const session = useStoreState(store => store.session);

	const { tableProps } = useFrontendTable({
		id: "uploadShipsTable",
		defaultSize: "COMPACT",
		content: data.map((item, index) => ({ ...item, id: index })),
		saveForOrganizationEnabled: false,
	});

	return (
		<Table
			{...tableProps}
			highlightRow={row => row.valid ? "SUCCESS" : "ERROR"}
			columnResizingEnabled={false}
			columns={[ {
				id: "ship.name",
				title: intl.formatMessage({ id: "SHIPS_NAME" }),
				disableFlexGrow: true,
				width: 150,
			}, {
				id: "ship.eni",
				title: intl.formatMessage({ id: "SHIP_ADD_ENI" }),
				disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "eni"),
				disableFlexGrow: true,
				width: 100,
			}, {
				id: "ship.mmsi",
				title: intl.formatMessage({ id: "SHIP_ADD_MMSI" }),
				disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "mmsi"),
				disableFlexGrow: true,
				width: 100,
			}, {
				id: "ship.type.name",
				title: intl.formatMessage({ id: "TYPE" }),
				disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "type"),
				disableFlexGrow: true,
				width: 150,
			}, {
				id: "exceptions",
				title: intl.formatMessage({ id: "ERRORS" }),
				render: row => {
					return (
						<ul style={{ margin: 0 }}>
							{row.exceptions.map((it:{ error: { code: string, message: string }, parameters: { message: string }[] }, index: number) => (
								<li key={index}>
									{it.error.code.includes("CI-100") ? it.parameters[0].message : it.error.message}
								</li>
							))}
						</ul>
					);
				},
			} ]} />
	);
};

export default UploadShipsDialog;
